<template>
    <PanelFrame ref="panelFrame" panel-class="payment-app" resize-directions="all">
        <template #titlebar-left>
            <span class="payment-titlebar-title">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="2" y="6" width="20" height="13" rx="2"/>
                    <line x1="2" y1="11" x2="22" y2="11"/>
                    <line x1="6" y1="15" x2="9" y2="15"/>
                </svg>
                {{ titlebarTitle }}
                <span v-if="titlebarSubtitle" class="payment-titlebar-subtitle">·  {{ titlebarSubtitle }}</span>
            </span>
        </template>

        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="onRefresh" :title="$t('common.transaction.actions.refresh')">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
            </button>
        </template>

        <template #default>
            <div class="payment-body">
                <div v-if="loading" class="payment-state">
                    <div class="payment-spinner"></div>
                </div>

                <div v-else-if="!transaction" class="payment-state">
                    <div class="payment-state-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                    </div>
                    <div>{{ $t('common.transaction.errors.record_not_found') }}</div>
                </div>

                <div v-else class="payment-scroll">
                    <!-- Summary -->
                    <div class="payment-summary">
                        <div class="payment-summary-head">
                            <div class="payment-summary-id">
                                <strong>#{{ transaction.id }}</strong>
                                <span class="payment-status" :class="'payment-status--' + transaction.status_id">
                                    {{ $t('common.transaction.status.' + transaction.status_id) }}
                                </span>
                                <span class="payment-direction-pill" :class="{'payment-direction-pill--expense': transaction.direction_id === 2}">
                                    <svg v-if="transaction.direction_id === 1" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                    <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                                        <polyline points="18 15 12 9 6 15"/>
                                    </svg>
                                    {{ $t('common.transaction.direction.' + transaction.direction_id) }}
                                </span>
                            </div>
                        </div>

                        <div class="payment-summary-cards">
                            <div class="payment-summary-card">
                                <span class="payment-summary-label">{{ $t('common.transaction.summary.total') }}</span>
                                <span class="payment-summary-amount">{{ formatTRY(transaction.total_amount) }}</span>
                            </div>
                            <div class="payment-summary-card payment-summary-card--paid">
                                <span class="payment-summary-label">{{ $t('common.transaction.summary.paid') }}</span>
                                <span class="payment-summary-amount">{{ formatTRY(transaction.paid_amount) }}</span>
                            </div>
                            <div class="payment-summary-card payment-summary-card--remaining">
                                <span class="payment-summary-label">{{ $t('common.transaction.summary.remaining') }}</span>
                                <span class="payment-summary-amount">{{ formatTRY(transaction.remaining_amount) }}</span>
                            </div>
                        </div>

                        <!-- Source row -->
                        <div v-if="transaction.source_type" class="payment-source-row">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                            </svg>
                            <span>{{ $t('common.transaction.source.label') }}:</span>
                            <strong>{{ sourceLabel }} #{{ transaction.source_id }}</strong>
                            <a class="payment-source-link" @click="onSourceClick">
                                {{ $t('common.transaction.source.goTo') }}
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                                    <line x1="7" y1="17" x2="17" y2="7"/>
                                    <polyline points="7 7 17 7 17 17"/>
                                </svg>
                            </a>
                            <span class="payment-badge">{{ $t('common.transaction.badge.modulePill') }}</span>
                        </div>
                        <div v-else class="payment-source-row">
                            <strong>{{ $t('common.transaction.source.manual') }}</strong>
                            <span class="payment-badge" style="margin-left: auto;">{{ $t('common.transaction.badge.manualPill') }}</span>
                        </div>

                        <div v-if="transaction.note" class="payment-note">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" style="flex-shrink: 0; margin-top: 1px;">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                <line x1="16" y1="13" x2="8" y2="13"/>
                                <line x1="16" y1="17" x2="8" y2="17"/>
                            </svg>
                            <span>{{ transaction.note }}</span>
                        </div>

                        <!-- Over-paid alert -->
                        <div v-if="overPaidAmount > 0" class="payment-alert payment-alert--warning">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0; margin-top: 1px;">
                                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                            <span>{{ $t('common.transaction.alerts.overPaid', { amount: formatTRY(overPaidAmount.toFixed(2)) }) }}</span>
                        </div>
                    </div>

                    <!-- Payments section -->
                    <div class="payment-section">
                        <div class="payment-section-head">
                            <div class="payment-section-title">
                                {{ $t('common.transaction.payment.title') }}
                                <span class="payment-section-count">{{ $t('common.transaction.payment.count', { count: (transaction.payments || []).length }) }}</span>
                            </div>
                            <div class="payment-section-actions">
                                <button v-if="canCreateInstallment" class="pay-btn pay-btn--sm" @click="openInstallmentForm">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                                        <line x1="16" y1="2" x2="16" y2="6"/>
                                        <line x1="8" y1="2" x2="8" y2="6"/>
                                        <line x1="3" y1="10" x2="21" y2="10"/>
                                    </svg>
                                    {{ $t('common.transaction.installment.create') }}
                                </button>
                                <button class="pay-btn pay-btn--sm pay-btn--primary"
                                    :disabled="!canAddPayment"
                                    :title="!canAddPayment && transaction.status_id !== 3 && transaction.status_id !== 4 ? $t('common.transaction.payment.allocationFull') : null"
                                    @click="openAddPaymentForm">
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6">
                                        <line x1="12" y1="5" x2="12" y2="19"/>
                                        <line x1="5" y1="12" x2="19" y2="12"/>
                                    </svg>
                                    {{ $t('common.transaction.payment.add') }}
                                </button>
                            </div>
                        </div>

                        <!-- Payment Form Drawer -->
                        <div v-if="activeForm === 'payment'" class="payment-form-drawer">
                            <div class="payment-form-head">
                                <span class="payment-form-title">
                                    {{ editingPaymentId ? $t('common.transaction.payment.editTitle') : $t('common.transaction.payment.addTitle') }}
                                </span>
                                <button class="pay-icon-btn" @click="closeForm">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>

                            <div class="payment-form-grid">
                                <div class="payment-form-row">
                                    <label class="payment-form-label">{{ $t('common.transaction.payment.amount') }}</label>
                                    <input class="payment-form-input"
                                        v-model="state.formAmount"
                                        :class="{'payment-form-input--error': amountExceedsAllocatable}"
                                        type="number" step="0.01" min="0.01" :max="allocatableAmount"
                                        :placeholder="$t('common.transaction.payment.amountPlaceholder')"/>
                                    <span v-if="amountExceedsAllocatable" class="payment-form-hint payment-form-hint--error">
                                        {{ $t('common.transaction.payment.amountExceedsHint') }}
                                    </span>
                                    <span v-else class="payment-form-hint">
                                        {{ $t('common.transaction.payment.amountMaxHint', { amount: formatTRY(allocatableAmount.toFixed(2)) }) }}
                                    </span>
                                </div>
                                <div class="payment-form-row">
                                    <label class="payment-form-label">{{ $t('common.transaction.payment.method') }}</label>
                                    <select class="payment-form-select" v-model="state.formMethodId">
                                        <option :value="null" disabled>{{ $t('common.transaction.payment.methodPlaceholder') }}</option>
                                        <option v-for="m in state.paymentMethods" :key="m.id" :value="m.id">{{ methodLabel(m) }}</option>
                                    </select>
                                </div>
                                <div class="payment-form-row payment-form-row--span">
                                    <label class="payment-form-label">{{ $t('common.transaction.payment.timing') }}</label>
                                    <div class="payment-form-segment">
                                        <button type="button"
                                            class="payment-form-segment-btn"
                                            :class="{'payment-form-segment-btn--active': state.formPaidAtNow}"
                                            @click="state.formPaidAtNow = true">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                                <polyline points="22 4 12 14.01 9 11.01"/>
                                            </svg>
                                            {{ $t('common.transaction.payment.timingNow') }}
                                        </button>
                                        <button type="button"
                                            class="payment-form-segment-btn"
                                            :class="{'payment-form-segment-btn--active': !state.formPaidAtNow}"
                                            @click="state.formPaidAtNow = false">
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                                <rect x="3" y="4" width="18" height="18" rx="2"/>
                                                <line x1="16" y1="2" x2="16" y2="6"/>
                                                <line x1="8" y1="2" x2="8" y2="6"/>
                                                <line x1="3" y1="10" x2="21" y2="10"/>
                                            </svg>
                                            {{ $t('common.transaction.payment.timingDeferred') }}
                                        </button>
                                    </div>
                                    <div class="payment-form-segment-hint">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <circle cx="12" cy="12" r="10"/>
                                            <line x1="12" y1="16" x2="12" y2="12"/>
                                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                                        </svg>
                                        <span>{{ state.formPaidAtNow ? $t('common.transaction.payment.timingNowHint') : $t('common.transaction.payment.timingDeferredHint') }}</span>
                                    </div>
                                </div>
                                <template v-if="state.formPaidAtNow">
                                    <div class="payment-form-row payment-form-row--span">
                                        <label class="payment-form-label">{{ $t('common.transaction.payment.cashRegister') }}</label>
                                        <select class="payment-form-select" v-model="state.formCashRegisterId">
                                            <option v-for="c in state.cashRegisters" :key="c.id" :value="c.id">
                                                {{ c.name }}<span v-if="c.is_default"> {{ $t('common.transaction.payment.cashRegisterDefault') }}</span>
                                            </option>
                                        </select>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="payment-form-row payment-form-row--span">
                                        <label class="payment-form-label">{{ $t('common.transaction.payment.dueDate') }}</label>
                                        <DateTimePicker v-model="state.formDueDate" type="date"
                                            :placeholder="$t('common.chooseDate')"
                                            :min="todayDate"
                                            clearable
                                            auto-modal/>
                                    </div>
                                </template>
                                <div class="payment-form-row payment-form-row--span">
                                    <label class="payment-form-label">{{ $t('common.transaction.payment.note') }}</label>
                                    <textarea class="payment-form-textarea" v-model="state.formNote"
                                        :placeholder="$t('common.transaction.payment.notePlaceholder')"></textarea>
                                </div>
                            </div>

                            <div class="payment-form-foot">
                                <button class="pay-btn" :disabled="state.saving" @click="closeForm">{{ $t('common.cancel') }}</button>
                                <button class="pay-btn pay-btn--primary" :disabled="!canSubmitPayment || state.saving" @click="submitPaymentForm">
                                    {{ state.saving ? $t('messages.wait') : $t('common.save') }}
                                </button>
                            </div>
                        </div>

                        <!-- Installment Form Drawer -->
                        <div v-if="activeForm === 'installment'" class="payment-form-drawer">
                            <div class="payment-form-head">
                                <span class="payment-form-title">{{ $t('common.transaction.installment.createTitle') }}</span>
                                <button class="pay-icon-btn" @click="closeForm">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                            <div class="payment-form-grid">
                                <div class="payment-form-row">
                                    <label class="payment-form-label">{{ $t('common.transaction.installment.count') }}</label>
                                    <input class="payment-form-input" v-model="state.installmentCount" type="number" min="2" max="48" step="1"/>
                                </div>
                                <div class="payment-form-row">
                                    <label class="payment-form-label">{{ $t('common.transaction.installment.startDate') }}</label>
                                    <DateTimePicker v-model="state.installmentStartDate" type="date"
                                        :placeholder="$t('common.transaction.installment.startDateHint')"
                                        :min="todayDate"
                                        clearable
                                        auto-modal/>
                                </div>
                            </div>
                            <div class="installment-preview">
                                <div>{{ $t('common.transaction.installment.remaining') }}: <strong>{{ formatTRY(allocatableAmount.toFixed(2)) }}</strong></div>
                                <div>{{ $t('common.transaction.installment.perInstallment') }}: <strong>{{ formatTRY(installmentPerAmount) }}</strong></div>
                                <div v-if="installmentLastDiff !== installmentPerAmount" style="opacity: 0.8;">
                                    {{ $t('common.transaction.installment.lastInstallmentDiff') }} ({{ formatTRY(installmentLastDiff) }})
                                </div>
                            </div>
                            <div class="payment-form-foot">
                                <button class="pay-btn" :disabled="state.saving" @click="closeForm">{{ $t('common.cancel') }}</button>
                                <button class="pay-btn pay-btn--primary" :disabled="!canSubmitInstallment || state.saving" @click="submitInstallment">
                                    {{ state.saving ? $t('messages.wait') : $t('common.transaction.installment.create') }}
                                </button>
                            </div>
                        </div>

                        <!-- Payment list -->
                        <div class="payment-list">
                            <div v-if="!transaction.payments || transaction.payments.length === 0" class="payment-list-empty">
                                <div class="payment-list-empty-icon">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                                        <rect x="2" y="6" width="20" height="13" rx="2"/>
                                        <line x1="2" y1="11" x2="22" y2="11"/>
                                    </svg>
                                </div>
                                <div class="payment-list-empty-title">{{ $t('common.transaction.payment.empty') }}</div>
                                <div class="payment-list-empty-hint">{{ $t('common.transaction.payment.emptyHint') }}</div>
                            </div>
                            <template v-for="p in sortedPayments" :key="p.id">
                            <div class="payment-row" :class="{'payment-row--popover-open': state.markPaidPopoverId === p.id}">
                                <div class="payment-row-icon" :class="paymentRowIconClass(p)">
                                    <svg v-if="p.status_id === 1" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
                                        <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                    <svg v-else-if="p.status_id === 0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <polyline points="12 6 12 12 16 14"/>
                                    </svg>
                                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                                        <circle cx="12" cy="12" r="10"/>
                                        <line x1="15" y1="9" x2="9" y2="15"/>
                                        <line x1="9" y1="9" x2="15" y2="15"/>
                                    </svg>
                                </div>
                                <div class="payment-row-main">
                                    <div class="payment-row-line1">
                                        <span class="payment-row-method">{{ methodLabel(p.payment_method) }}</span>
                                        <span v-if="p.installment_no" class="payment-row-installment">
                                            · {{ $t('common.transaction.payment.installmentNo', { no: p.installment_no }) }}
                                        </span>
                                        <span class="payment-status pay-row-status" :class="'pay-row-status--' + p.status_id">
                                            {{ $t('common.transaction.payment.paymentStatus.' + p.status_id) }}
                                        </span>
                                    </div>
                                    <div class="payment-row-line2">
                                        <span v-if="p.paid_at">{{ formatDateTime(p.paid_at) }}</span>
                                        <span v-else-if="p.due_date">{{ $t('common.transaction.payment.dueDate') }}: {{ formatDate(p.due_date) }}</span>
                                        <template v-if="p.cash_register_id">
                                            <span class="dot"></span>
                                            <span>{{ cashRegisterName(p.cash_register_id) }}</span>
                                        </template>
                                        <template v-if="p.note">
                                            <span class="dot"></span>
                                            <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 200px;">{{ p.note }}</span>
                                        </template>
                                    </div>
                                </div>
                                <div class="payment-row-amount">{{ formatTRY(p.amount) }}</div>
                                <div class="payment-row-actions">
                                    <button v-if="p.status_id === 0" class="pay-btn pay-btn--sm" @click="onMarkPaid(p)">
                                        {{ $t('common.transaction.payment.markPaid') }}
                                    </button>
                                    <button class="pay-icon-btn" @click="onEditPayment(p)" :title="$t('common.transaction.payment.edit')">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                        </svg>
                                    </button>
                                    <button class="pay-icon-btn pay-icon-btn--danger" @click="onDeletePayment(p)" :title="$t('common.transaction.payment.delete')">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                            <polyline points="3 6 5 6 21 6"/>
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                            <line x1="10" y1="11" x2="10" y2="17"/>
                                            <line x1="14" y1="11" x2="14" y2="17"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            <div v-if="state.markPaidPopoverId === p.id" class="payment-markpaid-drawer">
                                <div class="payment-markpaid-grid">
                                    <div class="payment-form-row">
                                        <label class="payment-form-label">{{ $t('common.transaction.payment.cashRegister') }}</label>
                                        <select class="payment-form-select" v-model="state.markPaidCashRegisterId">
                                            <option v-for="c in state.cashRegisters" :key="c.id" :value="c.id">
                                                {{ c.name }}<span v-if="c.is_default"> {{ $t('common.transaction.payment.cashRegisterDefault') }}</span>
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="payment-markpaid-foot">
                                    <button class="pay-btn pay-btn--sm" :disabled="state.saving" @click="closeMarkPaidPopover">{{ $t('common.cancel') }}</button>
                                    <button class="pay-btn pay-btn--sm pay-btn--primary" :disabled="state.saving" @click="confirmMarkPaid">
                                        {{ state.saving ? $t('messages.wait') : $t('common.transaction.payment.markPaid') }}
                                    </button>
                                </div>
                            </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </PanelFrame>
</template>

<script>
import './payment-panel.css'
import { createPanelMixin } from '@/components/panel/usePanelBase'
import PanelFrame from '@/components/panel/PanelFrame.vue'
import DateTimePicker from '@/components/common/date-time-picker/DateTimePicker.vue'
import transactionService from './transactionService'
import { resolveSourceLabelKey, resolveSourceRoute } from '@/core/services/sourceResolver'

const paymentPanelMixin = createPanelMixin({
    panelId: 'paymentPanel',
    minWidth: 480,
    minHeight: 480,
    defaults: {
        floatingMode: false,
        width: 760,
        height: 720,
    },
    hasInnerLayout: false,
    onContext(vm, ctx) {
        if (!ctx) return
        if (ctx.transactionId) {
            transactionService.loadByTransactionId(ctx.transactionId)
        } else if (ctx.sourceType && ctx.sourceId) {
            transactionService.loadBySource({ sourceType: ctx.sourceType, sourceId: ctx.sourceId })
        }
    },
    onClose() {
        transactionService.close()
    },
})

export default {
    name: 'PaymentPanel',
    mixins: [paymentPanelMixin],
    components: { PanelFrame, DateTimePicker },
    data() {
        return {
            state: transactionService.state,
        }
    },
    computed: {
        loading() { return this.state.loading },
        transaction() { return this.state.transaction },
        activeForm() { return this.state.activeForm },
        editingPaymentId() { return this.state.editingPaymentId },

        titlebarTitle() {
            if (!this.transaction) return this.$t('common.transaction.title')
            return this.$t('common.transaction.title') + ' #' + this.transaction.id
        },
        titlebarSubtitle() {
            if (!this.transaction) return null
            return this.$t('common.transaction.direction.' + this.transaction.direction_id)
        },
        sourceLabel() {
            const type = this.transaction?.source_type
            if (!type) return ''
            const labelKey = resolveSourceLabelKey(type) || ('common.transaction.sourceModules.' + type)
            const translated = this.$t(labelKey)
            return translated === labelKey ? type : translated
        },
        sourceRoute() {
            const t = this.transaction
            if (!t?.source_type) return null
            return resolveSourceRoute(t.source_type, t.source_id)
        },
        overPaidAmount() {
            if (!this.transaction) return 0
            const paid = parseFloat(this.transaction.paid_amount) || 0
            const total = parseFloat(this.transaction.total_amount) || 0
            return Math.max(0, paid - total)
        },
        sortedPayments() {
            if (!this.transaction?.payments) return []
            return [...this.transaction.payments].sort((a, b) => {
                if (a.installment_no && b.installment_no) return a.installment_no - b.installment_no
                if (a.installment_no) return 1
                if (b.installment_no) return -1
                const at = a.paid_at || a.created_at || ''
                const bt = b.paid_at || b.created_at || ''
                return at < bt ? -1 : (at > bt ? 1 : 0)
            })
        },
        allocatedAmount() {
            return transactionService.getAllocatedAmount(this.editingPaymentId)
        },
        allocatableAmount() {
            return transactionService.getAllocatableAmount(this.editingPaymentId)
        },
        amountExceedsAllocatable() {
            const amount = parseFloat(this.state.formAmount)
            if (isNaN(amount)) return false
            return amount > this.allocatableAmount + 0.001
        },
        canAddPayment() {
            if (!this.transaction) return false
            if (this.transaction.status_id === 3 || this.transaction.status_id === 4) return false
            return this.allocatableAmount > 0
        },
        canCreateInstallment() {
            if (!this.transaction) return false
            if (this.transaction.direction_id !== 1) return false
            if (this.transaction.status_id === 3 || this.transaction.status_id === 4) return false
            return this.allocatableAmount > 0
        },
        canSubmitPayment() {
            const amount = parseFloat(this.state.formAmount)
            if (!(amount > 0)) return false
            if (!this.state.formMethodId) return false
            if (this.amountExceedsAllocatable) return false
            return true
        },
        canSubmitInstallment() {
            const c = parseInt(this.state.installmentCount, 10)
            return c >= 2 && c <= 48 && this.allocatableAmount > 0
        },
        installmentPerAmount() {
            const c = parseInt(this.state.installmentCount, 10)
            const r = this.allocatableAmount
            if (!(c >= 2 && c <= 48) || !(r > 0)) return '0.00'
            return (Math.round((r / c) * 100) / 100).toFixed(2)
        },
        todayDate() {
            const d = new Date()
            d.setHours(0, 0, 0, 0)
            return d
        },
        installmentLastDiff() {
            const c = parseInt(this.state.installmentCount, 10)
            const r = this.allocatableAmount
            if (!(c >= 2 && c <= 48) || !(r > 0)) return '0.00'
            const per = Math.round((r / c) * 100) / 100
            const last = Math.round((r - per * (c - 1)) * 100) / 100
            return last.toFixed(2)
        },
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount)
            if (isNaN(n)) return '-'
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 })
        },
        formatDate(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr)
            if (isNaN(d.getTime())) return dateStr
            return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        },
        formatDateTime(dateStr) {
            if (!dateStr) return ''
            const d = new Date(dateStr.replace(' ', 'T'))
            if (isNaN(d.getTime())) return dateStr
            return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                + ' · ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
        },
        methodLabel(method) {
            if (!method) return '-'
            const key = 'common.transaction.payment.methodCode.' + method.code
            const trans = this.$t(key)
            return trans === key ? method.name : trans
        },
        cashRegisterName(id) {
            return transactionService.cashRegisterById(id)?.name || '-'
        },
        paymentRowIconClass(p) {
            if (p.status_id === 1) return 'payment-row-icon--completed'
            if (p.status_id === 0) return 'payment-row-icon--pending'
            if (p.status_id === 2) return 'payment-row-icon--failed'
            if (p.status_id === 3) return 'payment-row-icon--refunded'
            return ''
        },

        onRefresh() {
            if (this.transaction?.id) {
                transactionService.loadByTransactionId(this.transaction.id)
            }
        },
        onSourceClick() {
            const route = this.sourceRoute
            if (!route) return
            this.close()
            this.$nextTick(() => this.$router.push(route))
        },
        openAddPaymentForm() { transactionService.openAddPaymentForm() },
        openInstallmentForm() { transactionService.openInstallmentForm() },
        closeForm() { transactionService.closeForm() },
        submitPaymentForm() { transactionService.submitPaymentForm() },
        submitInstallment() { transactionService.createInstallmentPlan() },
        onEditPayment(p) { transactionService.openEditPaymentForm(p.id) },
        onDeletePayment(p) {
            this.eventBus?.$emit?.('showConfirmDialog', {
                title: this.$t('common.transaction.confirm.deletePaymentTitle'),
                message: this.$t('common.transaction.confirm.deletePaymentDesc'),
                confirmText: this.$t('common.delete'),
                confirmType: 'danger',
                onConfirm: () => transactionService.deletePayment(p.id),
            })
        },
        onMarkPaid(p) {
            transactionService.openMarkPaidPopover(p.id)
        },
        confirmMarkPaid() {
            transactionService.confirmMarkPaid()
        },
        closeMarkPaidPopover() {
            transactionService.closeMarkPaidPopover()
        },
    },
}
</script>
